import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { BETA_KEYS, FEATURE_FLAGS, LOCAL_STORAGE } from '@/constants';
import { POSTHOG_ENABLED } from './posthog-provider';
import posthog from 'posthog-js';
import {
    canSwitchComposeVersion,
    canSwitchInsightsVersion,
    isFenixOnlyWorkspace,
    isUsingComposeBeta,
    isUsingInsightsBeta,
} from '@/utils/beta/version-cohorts';
import {
    BarChartRounded,
    CampaignRounded,
    CreateRounded,
    DateRangeRounded,
    HomeRounded,
    InboxRounded,
    ListAltRounded,
    SettingsRounded,
    SpaceDashboardRounded,
    TodayRounded,
} from '@mui/icons-material';
import { useSession } from './session-provider';
import { isSwonkieDevWorkspace, isBrandPricingWorkspace, isFenixExclusive } from '@/utils/users-path-filter';
import { matchPath, useLocation } from 'react-router-dom';

export type Menu = {
    name: string;
    icon: ReactNode;
    path: string;
    dataId?: string;
    sub?: Submenu[];
    secondary?: boolean;
    hide?: boolean;
    beta?: string;
    betaEnabled?: boolean;
    classic?: boolean;
};

type Submenu = {
    subheader?: string;
    sub: {
        name: string;
        path: string;
        dataId?: string;
        sub?: { name: string; dataId?: string; path: string }[];
    }[];
};

const Context = createContext<{
    menu: Menu[];
    getMenuPath: (menu: Menu) => string;
} | null>(null);

export function MenuProvider({ children }: { children: React.ReactNode }) {
    const { workspace } = useSession();
    const isBrandPricing = isBrandPricingWorkspace(workspace);
    const [enabledBetas, setEnabledBetas] = useState<string | null>(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem(LOCAL_STORAGE.ENABLED_BETAS);
        }
        return null;
    });
    const { pathname } = useLocation();

    const usingComposeBeta = isUsingComposeBeta(workspace);
    const usingInsightsBeta = isUsingInsightsBeta(workspace);

    // Only labels the menu as beta for workspaces that can actually switch version. Forced-fenix
    // cohorts have no choice to make, so for them it isn't a beta and carries no badge.
    const composeBetaKey = canSwitchComposeVersion(workspace) ? BETA_KEYS.COMPOSE_BETA : undefined;
    const insightsBetaKey = canSwitchInsightsVersion(workspace) ? BETA_KEYS.REPORTS_BETA : undefined;

    //Localstorage event listener to update menu when beta is toggled in another tab
    useEffect(() => {
        const handler = () => {
            const changedStorage = enabledBetas !== localStorage.getItem(LOCAL_STORAGE.ENABLED_BETAS);
            if (changedStorage) {
                setEnabledBetas(localStorage.getItem(LOCAL_STORAGE.ENABLED_BETAS));
            }
        };
        window.addEventListener('beta-toggle', handler);
        return () => window.removeEventListener('beta-toggle', handler);
    }, [enabledBetas]);

    const calendarViewPath = matchPath({ path: '/posts/:viewId', end: false }, pathname);

    const menu: Menu[] = [
        {
            name: 'navigation.home',
            icon: <HomeRounded />,
            path:
                (POSTHOG_ENABLED && posthog.isFeatureEnabled(FEATURE_FLAGS.COMPOSE_BETA)) ||
                isSwonkieDevWorkspace(workspace) ||
                isFenixExclusive(workspace) ||
                isFenixOnlyWorkspace(workspace)
                    ? '/main'
                    : '/home',
        },
        ...(usingComposeBeta
            ? [
                  {
                      name: 'navigation.planning',
                      icon: <TodayRounded />,
                      path: calendarViewPath ? calendarViewPath.pathname : '/posts/calendar',
                      beta: composeBetaKey,
                      betaEnabled: true,
                  },
              ]
            : [
                  {
                      name: 'navigation.planning',
                      icon: <DateRangeRounded />,
                      path: '/planning',
                      beta: composeBetaKey,
                      betaEnabled: false,
                      sub: [
                          {
                              subheader: 'navigation.planning',
                              sub: [{ name: 'navigation.planning.calendar', path: '/calendar' }],
                          },
                          {
                              subheader: 'navigation.planning.workflow',
                              sub: [
                                  {
                                      name: 'navigation.planning.approval-status',
                                      path: '/posts-workflow',
                                  },
                                  {
                                      name: 'navigation.planning.staging',
                                      path: '/workflow-pool',
                                  },
                              ],
                          },
                          {
                              subheader: 'navigation.planning.content',
                              sub: [
                                  {
                                      name: 'navigation.planning.drafts',
                                      path: '/drafts',
                                  },
                              ],
                          },
                      ],
                  },
                  {
                      name: 'navigation.compose',
                      icon: <CreateRounded />,
                      path: '/produce-content',
                      beta: composeBetaKey,
                      betaEnabled: false,
                      hide: true,
                  },
              ]),
        ...(usingInsightsBeta
            ? [
                  {
                      name: 'navigation.dashboards',
                      icon: <SpaceDashboardRounded />,
                      path: '/dashboards',
                      beta: insightsBetaKey,
                      betaEnabled: true,
                  },
              ]
            : [
                  {
                      name: 'navigation.reports',
                      icon: <BarChartRounded />,
                      path: '/reports',
                      beta: insightsBetaKey,
                      betaEnabled: false,
                      sub: [
                          {
                              subheader: 'navigation.reports.personal',
                              sub: [
                                  { name: 'navigation.reports.basic', path: '/basic' },
                                  { name: 'navigation.reports.advanced', path: '/advanced' },
                                  { name: 'navigation.reports.competitors', path: '/competitors' },
                              ],
                          },
                          {
                              subheader: 'navigation.reports.paid-media',
                              sub: [
                                  { name: 'navigation.reports.ads-overview', path: '/ads-overview' },
                                  {
                                      name: 'navigation.reports.campaign-performance',
                                      path: '/ads-campaigns',
                                  },
                              ],
                          },
                      ],
                  },
              ]),
        {
            name: 'navigation.feeds',
            icon: <ListAltRounded />,
            path: '/feeds',
            sub: [
                {
                    subheader: 'navigation.feeds',
                    sub: [
                        { name: 'navigation.feeds.my-feed', path: '/posts' },
                        {
                            name: 'navigation.feeds.competitors',
                            path: '/competitor-monitoring',
                        },
                        { name: 'navigation.feeds.rss', path: '/rss-feed' },
                    ],
                },
                {
                    subheader: 'navigation.feeds.records',
                    sub: [{ name: 'navigation.feeds.publishing-record', path: '/my-posts' }],
                },
            ],
        },
        { name: 'navigation.inbox', icon: <InboxRounded />, path: '/inbox' },
        { name: 'navigation.listening', icon: <CampaignRounded />, path: '/listening' },
        {
            name: 'navigation.settings',
            icon: <SettingsRounded />,
            path: '/settings',
            dataId: 'menu-settings',
            secondary: true,
            sub: [
                {
                    subheader: 'navigation.settings.personal',
                    sub: [
                        {
                            name: 'navigation.settings.personal.general',
                            path: '/personal/general',
                        },
                        {
                            name: 'navigation.settings.personal.security',
                            path: '/personal/security',
                        },
                        {
                            name: 'navigation.settings.personal.privacy',
                            path: '/personal/privacy',
                        },
                        {
                            name: 'navigation.settings.personal.notifications',
                            path: '/personal/notifications',
                        },
                    ],
                },
                {
                    subheader: 'navigation.settings.workspace',
                    sub: [
                        {
                            name: 'navigation.settings.workspace.general',
                            path: '/workspace/general',
                        },
                        {
                            name: 'navigation.settings.workspace.settings',
                            path: '/workspace/settings',
                        },
                        {
                            name: 'navigation.settings.workspace.account-status',
                            path: '/workspace/account-status',
                        },
                        ...(isBrandPricing
                            ? []
                            : [
                                  {
                                      name: 'navigation.settings.workspace.social-profiles',
                                      path: '/workspace/social-profile',
                                  },
                              ]),
                        {
                            name: isBrandPricing
                                ? 'navigation.settings.workspace.groups-brand'
                                : 'navigation.settings.workspace.groups',
                            path: isBrandPricing ? '/workspace/brands' : '/workspace/groups',
                        },
                        {
                            name: 'navigation.settings.workspace.team',
                            path: '/workspace/team',
                        },
                        {
                            name: 'navigation.settings.workspace.billing',
                            path: '/workspace/billing',
                        },
                        {
                            name: 'navigation.settings.workspace.integrations',
                            path: '/workspace/integrations',
                        },
                        {
                            name: 'navigation.settings.workspace.rss',
                            path: '/workspace/rss-content',
                        },
                        {
                            name: 'navigation.settings.workspace.website-api',
                            path: '/workspace/api-keys',
                        },
                        {
                            name: 'navigation.settings.workspace.api',
                            path: '/workspace/public-api',
                        },
                    ],
                },
            ],
        },
    ];

    const getMenuPath = (menu: Menu) => {
        let menuPath = menu.path;
        if (menu.sub) {
            if (menu.sub[0].sub) {
                menuPath += menu.sub[0].sub[0].path;
            }
        }
        return menuPath;
    };

    return <Context.Provider value={{ menu, getMenuPath }}>{children}</Context.Provider>;
}

export function useMenu() {
    const currentContext = useContext(Context);
    if (!currentContext) {
        throw new Error('useMenu must be used within a MenuProvider');
    }
    return currentContext;
}
